<template lang="pug">
  SupplierLayout
    CommonSection.b-supplier-request-page(title="Запрос КП" data-testid="supplier-request-page")
      template(v-if="card")
        ._head
          ._head-line
            span._status(data-testid="supplier-request-status") {{ requestStatusTitle(card.status) }}
            span._date(data-testid="supplier-request-date") {{ date(card.createdAt) }}
            span._designer(data-testid="supplier-request-designer") {{ card.designerName || 'Дизайнер' }}
            span._readonly(v-if="card.readOnly" data-testid="supplier-request-readonly") только чтение
          ._head-actions
            Btn(grey sm data-testid="supplier-request-back" @click="back") ‹ К списку запросов
            Btn(grey sm data-testid="supplier-request-chat" @click="openChat(chats.supplier)") Чат с дизайнером
            Btn(
              v-if="chats.tripartite"
              grey
              sm
              data-testid="supplier-request-chat-tripartite"
              @click="openChat(chats.tripartite)"
              ) Чат с дизайнером и заказчиком

        ._files(data-testid="supplier-request-files")
          ._files-title Файлы КП
          ._files-empty(v-if="!quoteFiles.length") Файлов КП пока нет
          ._file(v-for="quoteFile in quoteFiles" :key="quoteFile.id" data-testid="supplier-request-file")
            span._file-name(data-testid="supplier-request-file-name") {{ quoteFile.file && quoteFile.file.name }}
            a._file-link(
              v-if="quoteFile.file && quoteFile.file.url"
              :href="quoteFile.file.url"
              target="_blank"
              rel="noopener noreferrer"
              ) скачать
            span._file-status(data-testid="supplier-request-file-status") {{ fileStatusText(quoteFile) }}
            button._file-cancel(
              v-if="writable && isMatchActive(quoteFile)"
              type="button"
              data-testid="supplier-request-file-cancel"
              @click="cancelMatch(quoteFile)"
              ) Отменить
            span._file-replaced(v-if="quoteFile.replacedItem")
              | вместо подтверждения: {{ quoteFile.replacedItem.name }}
            router-link._file-match(
              v-if="isMatchReady(quoteFile)"
              data-testid="supplier-request-file-match"
              :to="matchRoute(quoteFile)"
              ) Проверить сопоставление
          label._upload(v-if="writable")
            | Загрузить КП
            input._upload-input(
              type="file"
              data-testid="supplier-request-upload-input"
              @change="onQuoteFile"
              )

        ._items
          ._items-empty(v-if="!items.length") Позиций в запросе нет
          SupplierRequestItem(
            v-for="row in items"
            :key="row.id"
            ref="item"
            :row="row"
            :writable="writable"
            :readOnly="card.readOnly"
            :highlighted="routeItemId === itemIdOf(row)"
            :editingOfferId="editingOfferId"
            :addingForm="!!addingItemId && addingItemId === itemIdOf(row)"
            :historyOfferId="historyOfferId"
            :history="history"
            @add="startAdd(row)"
            @edit="startEdit"
            @cancel="closeForm"
            @save="saveOffer(row, $event)"
            @delete="deleteOffer"
            @withdraw="withdrawOffer"
            @confirm="confirmOffer(row, $event)"
            @history="openOfferHistory"
            @replace-quote="replaceByQuote(row, $event)"
            )
          ._offers-empty(v-if="items.length && !hasOffers" data-testid="supplier-request-offers-empty")
            | Откликов пока нет

        ._send(v-if="showSend")
          Btn(
            blue
            data-testid="supplier-request-send-offers"
            :disabled="!sendableCount"
            @click="sendOffers"
            ) Отправить отклики ({{ sendableCount }})
</template>

<script>
import SupplierLayout from '@/layouts/Supplier.vue';
import CommonSection from '@/components/CommonSection.vue';
import SupplierRequestItem from '@/components/supplier/SupplierRequestItem.vue';
import model, { POLL_INTERVAL, JOB_ACTIVE } from '@/model/supplierCabinet';
import { date, requestStatusTitle } from '@/utils/spec';

/**
 * Карточка запроса КП в кабинете поставщика (ТЗ 6.5): шапка со статусом, датой и дизайнером, кнопки
 * чатов по этой спецификации, блок «Файлы КП» с состоянием распознавания и ссылкой на экран
 * проверки сопоставления, таблица позиций запроса с откликами и кнопка «Отправить отклики (N)».
 *
 * В статусе «Завершён» и в архивном проекте операции записи не показываются (ТЗ 6.5, 6.9).
 * Состояние задания сопоставления опрашивается раз в 3 секунды (ТЗ 5.1). Сгенерировано Claude.
 */
export default {
  name: 'SupplierRequestPage',
  components: { SupplierLayout, CommonSection, SupplierRequestItem },
  data() {
    return {
      card: null,
      editingOfferId: null,
      addingItemId: null,
      historyOfferId: null,
      history: [],
      timer: null,
    };
  },
  computed: {
    requestId() { return Number(this.$route.params.requestId); },
    /** Позиция из адреса: переход «к позиции» из события чата и из уведомления (ТЗ 6.10, 6.8). */
    routeItemId() { return Number(this.$route.query.item) || null; },
    /**
     * Идентификаторы чатов этой спецификации — из карточки запроса (ТЗ 4.2, 6.5). Двусторонний
     * чат с дизайнером есть всегда (2.2), трёхсторонний — только когда дизайнер его создал:
     * пока его нет, кнопки «Чат с дизайнером и заказчиком» на экране тоже нет (6.9).
     */
    chats() { return this.card?.chats ?? { supplier: null, tripartite: null }; },
    items() { return this.card?.items ?? []; },
    quoteFiles() { return this.card?.quoteFiles ?? []; },
    finished() { return this.card?.status === 'finished'; },
    writable() { return !!this.card && !this.card.readOnly && !this.finished; },
    allOffers() {
      return this.items.reduce((acc, row) => [...acc, ...(row.offers ?? [])], []);
    },
    hasOffers() { return this.allOffers.length > 0; },
    drafts() {
      return this.items.reduce(
        (acc, row) => [...acc, ...(row.offers ?? [])
          .filter((offer) => offer.state === 'draft')
          .map((offer) => ({ offer, row }))],
        [],
      );
    },
    /**
     * Сколько черновиков уйдёт по кнопке «Отправить отклики (N)»: черновики по позициям, ушедшим
     * заказчику или в закупку, в отправку по умолчанию не входят (ТЗ 4.2, 6.5).
     */
    sendableCount() {
      return this.drafts.filter(({ row }) => row.offersAccepted !== false).length;
    },
    showSend() { return this.writable && this.drafts.length > 0; },
  },
  watch: {
    requestId: {
      immediate: true,
      handler() { this.load(); },
    },
    /** Тот же адрес, другая позиция: подсветить и показать её без перечитывания карточки (ТЗ 6.10). */
    routeItemId(id) {
      if (id) this.showRouteItem();
    },
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    date,
    requestStatusTitle,
    itemIdOf(row) { return row.item?.id ?? null; },

    async load(withPreloader = true) {
      this.card = await model.fetchCard(this.requestId, withPreloader);
      this.applyRouteForm();
      this.showRouteItem();
      this.syncPolling();
    },

    /** Тихое перечитывание после действия: свои признаки и состояния считает сервер (ТЗ 4.2). */
    async reload() {
      this.card = await model.fetchCard(this.requestId, false);
      this.syncPolling();
    },

    /** Форма нового отклика по ссылке с параметром `addOffer` (ТЗ 6.5). */
    applyRouteForm() {
      const itemId = Number(this.$route.query.addOffer) || null;
      if (itemId && this.items.some((row) => this.itemIdOf(row) === itemId)) {
        this.addingItemId = itemId;
      }
    },

    /**
     * Переход «к позиции»: параметр `item` в адресе подсвечивает позицию и показывает её (ТЗ 6.10,
     * 6.8). Тот же механизм, что у дизайнера в таблице спецификации: подсветку даёт свойство строки,
     * показ — прокрутка к её элементу.
     */
    showRouteItem() {
      const id = this.routeItemId;
      if (!id) return;
      this.$nextTick(() => {
        const rows = this.$refs.item ?? [];
        const row = rows.find((component) => component.row?.item?.id === id);
        if (row) row.$el.scrollIntoView({ block: 'center' });
      });
    },

    // ==== файлы КП ====

    fileStatusText(quoteFile) {
      const job = quoteFile.aiJob;
      if (!job) return 'без распознавания';
      if (JOB_ACTIVE.includes(job.status)) return 'сопоставление: в обработке';
      if (job.status === 'error') {
        return `ошибка: ${job.error || 'не удалось распознать файл'}`;
      }
      if (job.status === 'cancelled') return 'сопоставление отменено';
      return 'Готово';
    },
    isMatchActive(quoteFile) {
      return !!quoteFile.aiJob && JOB_ACTIVE.includes(quoteFile.aiJob.status);
    },
    /** «Отменить» сопоставление в работе (ТЗ 4.1): отклики можно добавить вручную. */
    async cancelMatch(quoteFile) {
      await model.cancelMatch(quoteFile.aiJob.id);
      await this.reload();
    },
    isMatchReady(quoteFile) {
      return quoteFile.aiJob?.status === 'done';
    },
    matchRoute(quoteFile) {
      return {
        name: 'supplier-match',
        params: { requestId: String(this.requestId), quoteFileId: String(quoteFile.id) },
      };
    },
    async onQuoteFile(event) {
      // ссылка на само поле: сбрасываем его сразу, иначе повторный выбор того же файла не даст change
      const input = event.target;
      const [file] = Array.from(input.files ?? []);
      input.value = '';
      if (!file) return;
      const bound = await model.uploadQuote(this.requestId, file);
      if (bound) await this.reload();
    },
    async replaceByQuote(row, file) {
      const bound = await model.replaceByQuote(this.requestId, row.item, file);
      if (bound) await this.reload();
    },

    /** Опрос заданий сопоставления, пока они в очереди или в работе (ТЗ 5.1, 5.5). */
    syncPolling() {
      const active = this.quoteFiles.some((f) => f.aiJob && JOB_ACTIVE.includes(f.aiJob.status));
      if (active) this.startPolling();
      else this.stopPolling();
    },
    startPolling() {
      if (this.timer) return;
      this.timer = setInterval(() => { this.reload(); }, POLL_INTERVAL);
    },
    stopPolling() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    // ==== отклики ====

    startAdd(row) {
      this.editingOfferId = null;
      this.addingItemId = this.itemIdOf(row);
    },
    startEdit(offer) {
      this.addingItemId = null;
      this.editingOfferId = offer.id;
    },
    closeForm() {
      this.addingItemId = null;
      this.editingOfferId = null;
      this.dropAddOfferParam();
    },

    /**
     * Просьба открыть форму отклика — разовая: пока параметр остаётся в адресе, перезагрузка страницы
     * открывала бы форму снова, поэтому при закрытии и после сохранения он убирается.
     */
    dropAddOfferParam() {
      if (!this.$route.query.addOffer) return;
      const query = { ...this.$route.query };
      delete query.addOffer;
      this.$router.replace({
        name: 'supplier-request',
        params: { requestId: String(this.requestId) },
        query,
      });
    },
    async saveOffer(row, { offer, params }) {
      const saved = offer
        ? await model.updateOffer(offer.id, params)
        : await model.createOffer(this.requestId, this.itemIdOf(row), params);
      if (!saved) return;
      this.closeForm();
      await this.reload();
      if (this.historyOfferId) await this.loadHistory(this.historyOfferId);
    },
    async deleteOffer(offer) {
      if (await model.deleteOffer(offer)) await this.reload();
    },
    async withdrawOffer(offer) {
      if (await model.withdrawOffer(offer)) await this.reload();
    },
    async confirmOffer(row, offer) {
      if (await model.confirmOffer(offer, row.item?.name ?? '')) await this.reload();
    },
    async sendOffers() {
      const card = await model.sendOffers(this.requestId, this.sendableCount);
      if (!card) return;
      this.card = card;
      this.syncPolling();
    },
    async openOfferHistory(offer) {
      if (this.historyOfferId === offer.id) {
        this.historyOfferId = null;
        this.history = [];
        return;
      }
      await this.loadHistory(offer.id);
    },
    async loadHistory(offerId) {
      this.history = await model.fetchOfferHistory(offerId);
      this.historyOfferId = offerId;
    },

    // ==== переходы ====

    back() {
      this.$router.push({ name: 'supplier-requests' });
    },
    /** Чат по этой спецификации (ТЗ 6.5, 6.10); если чата нет в карточке — список чатов. */
    openChat(chatId) {
      this.$router.push(chatId
        ? { name: 'supplier-chat', params: { chatId: String(chatId) } }
        : { name: 'supplier-chats' });
    },
  },
};
</script>

<style scoped lang="scss">
.supplier-request-page {
  &__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 20px;
  }

  &__head-line {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 6px 16px;
    font-size: 14px;
  }

  &__status {
    font-weight: 700;
  }

  &__date,
  &__designer {
    color: var(--color-middle-gray);
  }

  &__readonly {
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 10px;
    background: #F1F5FA;
    color: var(--color-middle-gray);
  }

  &__head-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  &__files {
    padding: 12px 14px;
    margin-bottom: 20px;
    border: 1px solid var(--color-super-light);
    border-radius: 8px;
    background: var(--color-white);
  }

  &__files-title {
    font-weight: 700;
    font-size: 14px;
    margin-bottom: 8px;
  }

  &__files-empty {
    font-size: 12px;
    color: var(--color-middle-gray);
  }

  &__file {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 14px;
    padding: 4px 0;
    font-size: 13px;
  }

  &__file-status,
  &__file-replaced {
    color: var(--color-middle-gray);
    font-size: 12px;
  }

  &__file-link,
  &__file-match {
    color: var(--color-accent);
  }

  &__file-cancel {
    border: none;
    background: none;
    font: inherit;
    font-size: 12px;
    color: var(--color-accent);
    cursor: pointer;
    padding: 0;
  }

  &__upload {
    display: inline-block;
    margin-top: 10px;
    color: var(--color-accent);
    font-size: 13px;
    cursor: pointer;
  }

  &__upload-input {
    position: absolute;
    opacity: 0;
    visibility: hidden;
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__items-empty,
  &__offers-empty {
    font-size: 13px;
    color: var(--color-middle-gray);
  }

  &__send {
    margin-top: 20px;
  }

  // Узкие экраны (общий порог модуля — 768 px): шапка карточки складывается в одну колонку,
  // кнопки чатов и возврата растягиваются на всю ширину
  @media (max-width: 768px) {
    &__head {
      flex-direction: column;
      align-items: stretch;
    }

    &__head-actions {
      flex-direction: column;
      align-items: stretch;
    }
  }
}
</style>
